<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <v-card data-cy="dialog-node-properties">
      <v-card-title class="d-flex justify-space-between mb-2" data-cy="dialog-node-properties-title">
        {{ $t('routes.node.propertiesNodeDialogTitle') }}

        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="text-body-1">
        <!-- Basic Information Section -->
        <div class="properties-section">
          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-folder-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogName') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">
              {{ node.name }}
            </v-col>
          </v-row>

          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-shape-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogType') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ nodeTypeName }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-lock-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogAccess') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ node.teamonly ? $t('common.teamonly') : $t('common.public') }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1" v-if="node.archived">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="warning">mdi-archive-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogArchived') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ $t('common.yes') }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-clock-time-four-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.retentionPeriod') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ retentionPeriodDisplay }}</v-col>
          </v-row>
        </div>

        <v-divider class="my-3" aria-hidden="true" />

        <!-- Storage Information Section (from API) -->
        <template v-if="loading">
          <v-row no-gutters>
            <v-col cols="12" class="text-center py-4">
              <v-progress-circular indeterminate color="primary" size="32" :aria-label="$t('common.loading')" />
            </v-col>
          </v-row>
        </template>

        <template v-else-if="error">
          <v-row no-gutters>
            <v-col cols="12" class="text-error py-2 d-flex align-center">
              <v-icon size="small" class="mr-2">mdi-alert-circle-outline</v-icon>
              {{ error }}
            </v-col>
          </v-row>
        </template>

        <template v-else>
          <div class="properties-section">
            <v-row no-gutters class="py-1">
              <v-col cols="5" class="d-flex align-center">
                <v-icon size="small" class="mr-2" color="primary">mdi-file-multiple-outline</v-icon>
                <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogFilesInFolder') }}</span>
              </v-col>
              <v-col cols="7" class="d-flex align-center text-break">{{ properties.filesInFolder || 0 }}</v-col>
            </v-row>

            <v-row no-gutters class="py-1">
              <v-col cols="5" class="d-flex align-center">
                <v-icon size="small" class="mr-2" color="primary">mdi-harddisk</v-icon>
                <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogBytesInFolder') }}</span>
              </v-col>
              <v-col cols="7" class="d-flex align-center text-break">{{ formatSize(properties.sizeInFolder || 0, 2) }}</v-col>
            </v-row>

            <v-row no-gutters class="py-1">
              <v-col cols="5" class="d-flex align-center">
                <v-icon size="small" class="mr-2" color="primary">mdi-folder-multiple-outline</v-icon>
                <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogSubfolders') }}</span>
              </v-col>
              <v-col cols="7" class="d-flex align-center text-break">{{ properties.subfoldersCount || 0 }}</v-col>
            </v-row>

            <v-row no-gutters class="py-1">
              <v-col cols="5" class="d-flex align-center">
                <v-icon size="small" class="mr-2" color="primary">mdi-file-tree-outline</v-icon>
                <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogFilesInSubfolders') }}</span>
              </v-col>
              <v-col cols="7" class="d-flex align-center text-break">{{ properties.filesInSubfolders || 0 }}</v-col>
            </v-row>

            <v-row no-gutters class="py-1">
              <v-col cols="5" class="d-flex align-center">
                <v-icon size="small" class="mr-2" color="primary">mdi-database-outline</v-icon>
                <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogBytesInSubfolders') }}</span>
              </v-col>
              <v-col cols="7" class="d-flex align-center text-break">{{ formatSize(properties.size || 0, 2) }}</v-col>
            </v-row>
          </div>
        </template>

        <v-divider class="my-3" aria-hidden="true" />

        <!-- Creation Information Section -->
        <div class="properties-section">
          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-account-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogCreatedBy') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ createdByName }}</v-col>
          </v-row>

          <v-row no-gutters class="py-1">
            <v-col cols="5" class="d-flex align-center">
              <v-icon size="small" class="mr-2" color="primary">mdi-calendar-outline</v-icon>
              <span class="font-weight-medium">{{ $t('routes.node.propertiesNodeDialogCreatedOn') }}</span>
            </v-col>
            <v-col cols="7" class="d-flex align-center text-break">{{ isoToDatetime(node.createdat) }}</v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="cancel" data-cy="dialog-node-properties-cancel-btn">{{ $t('common.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Api from '@/api/Api';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import {
  formatSize,
  isoToDatetime,
  truncateText,
} from '@/components/formatters.js';

export default {
  name: 'NodePropertiesDialog',
  mixins: [DialogActionKeys],
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    node: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      properties: {},
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    nodeTypes() {
      const commonStore = useCommonStore();
      return commonStore.nodeTypes;
    },
    nodeTypeName() {
      switch (this.node.type) {
        case this.nodeTypes.TENANT:
          return this.$t('routes.node.typeTenant');
        case this.nodeTypes.CLIENT:
          return this.$t('routes.node.typeClient');
        case this.nodeTypes.PROJECT:
          return this.$t('routes.node.typeProject');
        case this.nodeTypes.FOLDER:
          return this.$t('routes.node.typeFolder');
        default:
          return this.$t('routes.node.typeUnknown');
      }
    },
    createdByName() {
      if (this.node.createdby) {
        return `${this.node.createdby.firstname} ${this.node.createdby.lastname}`;
      }
      return this.$t('common.deletedUser');
    },
    retentionPeriodDisplay() {
      if (!this.node.retention?.hasRetention) {
        return this.$t('routes.node.noRetention');
      }
      const period = this.node.retentionperiod;
      const type = this.node.retentiontype?.toLowerCase();
      if (!period || !type) {
        return this.$t('routes.node.noRetention');
      }
      return `${period} ${this.$t('menus.createNode.' + type)}`;
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        this.fetchProperties();
      }
    },
  },
  methods: {
    formatSize,
    isoToDatetime,
    truncateText,
    cancel() {
      this.dialog = false;
    },
    async fetchProperties() {
      this.loading = true;
      this.error = null;
      try {
        const response = await Api.node.getNodeInfo(this.node.id);
        this.properties = response.data;
      } catch (err) {
        console.error('Failed to fetch node properties:', err);
        this.error = this.$t('routes.node.propertiesNodeDialogError');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.properties-section > .v-row:nth-child(even) {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.properties-section > .v-row {
  border-radius: 4px;
  padding-left: 8px;
  padding-right: 8px;
}
</style>
